// One conversation: header, message history (loads older pages as you scroll up), composer with mentions, emoji and
// attachments, the thread panel and the details panel.
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useMatch, useNavigate, useParams, useSearchParams } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../../lib/api";
import {
  MENTION, fileUrl, formatDay, formatSize, formatTime, keys, newClientId, uploadFile,
  type Attachment, type Conversation, type Message, type MessagePage,
} from "../../lib/chat";
import { addToPages, patchMessage, upsertMessage, useLive, useTyping } from "../../lib/live";
import { formatDuration, startCall, type CallMedia, type CallNavState } from "../../lib/calls";
import { Alert, Avatar, Badge, Button, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { atLeast, errorText, useOrg } from "../org";
import { AddPeopleDialog, PresenceDot, usePresence } from "./dialogs";
import { activeStatus, isDnd, useNow, usePerson } from "../../lib/people";
import { PersonButton } from "../profile";

const QUICK = ["👍", "❤️", "😂", "🎉", "😮", "🙏"];
const EMOJI = ["😀", "😁", "😂", "🤣", "😊", "😍", "😘", "😎", "🤔", "😅", "😬", "🙃", "😴", "😢", "😭", "😡", "🤯", "🥳", "😇", "🤝",
  "👍", "👎", "👏", "🙌", "🙏", "💪", "👀", "👋", "✌️", "🤞", "❤️", "🔥", "✨", "🎉", "✅", "❌", "⚠️", "💡", "📌", "🚀"];
const GROUP_MS = 5 * 60 * 1000;

type Replies = { root: Message; replies: Message[] };

export function ConversationView() {
  const { conversationId = "" } = useParams();
  return <ConversationScreen key={conversationId} conversationId={conversationId} />;
}

// `embedded`: inside the call screen (no header, details or call buttons).
export function ConversationScreen({ conversationId, embedded = false }: { conversationId: string; embedded?: boolean }) {
  const { org, me } = useOrg();
  const live = useLive();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const threadId = params.get("thread");
  const [details, setDetails] = useState(false);
  const [dragging, setDragging] = useState(false);
  const composer = useRef<ComposerHandle>(null);
  const base = `/orgs/${org.slug}/conversations/${conversationId}`;

  const conv = useQuery({ queryKey: keys.conversation(org.slug, conversationId), queryFn: () => api<{ conversation: Conversation }>(base), retry: false });
  const history = useInfiniteQuery({
    queryKey: keys.messages(org.slug, conversationId),
    queryFn: ({ pageParam }) => api<MessagePage>(`${base}/messages?limit=50${pageParam ? `&before=${pageParam}` : ""}`),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.hasMore ? last.messages[0]?.seq ?? undefined : undefined),
    enabled: conv.isSuccess,
  });

  useEffect(() => {
    live.setActiveConversation(conversationId);
    return () => live.setActiveConversation(null);
  }, [live, conversationId]);

  const messages = useMemo(() => [...(history.data?.pages ?? [])].reverse().flatMap((p) => p.messages), [history.data]);
  const c = conv.data?.conversation;
  const names = useMemo(() => new Map((c?.members ?? []).map((m) => [m.id, m.name])), [c?.members]);

  // Where "New messages" starts: fixed when the conversation opens.
  const [firstUnread, setFirstUnread] = useState<number | null>(null);
  useEffect(() => {
    if (c && firstUnread === null) setFirstUnread(c.lastSeq > c.lastReadSeq ? c.lastReadSeq + 1 : Infinity);
  }, [c, firstUnread]);

  // Read up to the newest message while it's on screen.
  const marked = useRef(0);
  const newestSeq = messages.reduce((n, m) => (m.seq && !m.pending && m.seq > n ? m.seq : n), 0);
  useEffect(() => {
    if (!c || !newestSeq) return;
    marked.current = Math.max(marked.current, c.lastReadSeq);
    const mark = () => {
      if (document.visibilityState !== "visible" || newestSeq <= marked.current) return;
      marked.current = newestSeq;
      api(`${base}/read`, { method: "POST", body: { seq: newestSeq } }).catch(() => { marked.current = 0; });
    };
    const t = setTimeout(mark, 300);
    document.addEventListener("visibilitychange", mark);
    return () => { clearTimeout(t); document.removeEventListener("visibilitychange", mark); };
  }, [c, newestSeq, base]);

  if (conv.error) {
    return (
      <div className="grid h-full place-items-center p-6 text-center">
        <div><p className="font-medium">{conv.error instanceof ApiError && conv.error.status === 404 ? "This conversation isn't available." : errorText(conv.error)}</p>
          <Link to={`/o/${org.slug}/chat`} className="mt-2 inline-block text-sm text-brand-600 hover:underline">Back to chat</Link></div>
      </div>
    );
  }
  if (!c) return <div className="grid h-full place-items-center text-brand-600"><Spinner /></div>;

  const openThread = (id: string) => { setDetails(false); setParams({ thread: id }); };
  const closeThread = () => setParams({});
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files.length) composer.current?.addFiles([...e.dataTransfer.files]); };

  return (
    <div className="flex h-full min-h-0">
      <div className="relative flex min-w-0 flex-1 flex-col" onDragOver={(e) => { if (e.dataTransfer.types.includes("Files") && !c.archived) { e.preventDefault(); setDragging(true); } }} onDragLeave={(e) => { if (e.currentTarget === e.target) setDragging(false); }} onDrop={onDrop}>
        {!embedded && <Header c={c} onDetails={() => { closeThread(); setDetails((d) => !d); }} />}
        <MessageList
          conversation={c} messages={messages} names={names} firstUnread={firstUnread}
          loadingOlder={history.isFetchingNextPage} hasOlder={Boolean(history.hasNextPage)} loading={history.isLoading}
          onOlder={() => { if (history.hasNextPage && !history.isFetchingNextPage) void history.fetchNextPage(); }}
          onThread={openThread}
        />
        <TypingLine conversationId={c.id} />
        {c.archived
          ? <div className="border-t border-slate-200 p-4 text-center text-sm text-slate-500 dark:border-slate-800">This conversation has been archived.</div>
          : <Composer ref={composer} conversation={c} placeholder={embedded ? "Message everyone in the chat" : c.kind === "channel" ? `Message #${c.title}` : `Message ${c.title}`} onSent={() => qc.invalidateQueries({ queryKey: keys.conversations(org.slug) })} autoFocus={!embedded} compact={embedded} />}
        {dragging && <div className="pointer-events-none absolute inset-2 grid place-items-center rounded-2xl border-2 border-dashed border-brand-500 bg-brand-50/80 text-brand-700 dark:bg-brand-700/30 dark:text-brand-100">Drop files to attach</div>}
      </div>
      {threadId && <ThreadPanel conversation={c} rootId={threadId} names={names} onClose={closeThread} />}
      {details && !threadId && !embedded && <DetailsPanel conversation={c} meId={me.user.id} onClose={() => setDetails(false)} />}
    </div>
  );
}

function Header({ c, onDetails }: { c: Conversation; onDetails: () => void }) {
  const { org, me } = useOrg();
  const presence = usePresence();
  const other = c.kind === "dm" ? c.members.find((m) => m.id !== me.user.id) ?? c.members[0] : undefined;
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-900">
      <Link to={`/o/${org.slug}/chat`} className="rounded-lg p-2 [@media(pointer:coarse)]:p-3 text-slate-500 hover:bg-slate-100 md:hidden dark:hover:bg-slate-800" aria-label="Back to conversations"><Icon name="arrowLeft" /></Link>
      {c.kind === "channel" ? <Icon name={c.isPrivate ? "lock" : "hash"} className="h-5 w-5 text-slate-400" /> : c.kind === "group"
        ? <span className="grid h-8 w-8 place-items-center rounded-full bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200"><Icon name="users" className="h-4 w-4" /></span>
        : <PersonButton userId={other!.id} label={c.title} className="relative"><Avatar name={c.title} userId={other?.id} size="sm" /><PresenceDot status={other ? presence[other.id] : undefined} userId={other?.id} className="absolute -bottom-0.5 -right-0.5" /></PersonButton>}
      <div className="min-w-0 flex-1">
        <h1 className="truncate font-semibold">{c.kind === "dm" && other ? <PersonButton userId={other.id} label={c.title} className="rounded hover:underline">{c.title}</PersonButton> : c.title}{c.team && <span className="font-normal text-slate-500"> · {c.team.name}</span>}{c.project && <Link to={`/o/${org.slug}/projects/${c.project.id}`} className="font-normal text-brand-600 hover:underline"> · open the project</Link>}</h1>
        {c.topic ? <p className="truncate text-xs text-slate-500">{c.topic}</p> : c.kind === "group" ? <p className="truncate text-xs text-slate-500">{c.members.length} people</p> : c.kind === "dm" && other ? <DmSubtitle userId={other.id} /> : null}
      </div>
      {c.muted && <Icon name="bellOff" className="h-4 w-4 text-slate-400" />}
      {!c.archived && <CallButtons c={c} />}
      <button type="button" onClick={onDetails} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800" aria-label="Conversation details">
        <Icon name="users" className="h-4 w-4" />{c.members.length}
      </button>
    </header>
  );
}

// Under a direct message's name: their status message, or Do not disturb.
function DmSubtitle({ userId }: { userId: string }) {
  const person = usePerson(userId);
  const now = useNow();
  const status = activeStatus(person?.status, now);
  if (isDnd(person?.dndUntil, now)) return <p className="truncate text-xs font-medium text-red-600 dark:text-red-400">Do not disturb{status ? ` · ${status.emoji ?? ""} ${status.text}` : ""}</p>;
  if (!status) return person?.jobTitle ? <p className="truncate text-xs text-slate-500">{person.jobTitle}</p> : null;
  return <p className="truncate text-xs text-slate-500">{status.emoji && <span aria-hidden="true">{status.emoji} </span>}{status.text}</p>;
}

// A person's status emoji (the message on hover), next to their name.
function StatusEmoji({ userId }: { userId: string }) {
  const person = usePerson(userId);
  const status = activeStatus(person?.status, useNow());
  if (!status) return null;
  return <span className="ml-1 cursor-default text-xs" title={status.text || undefined} role="img" aria-label={`Status: ${status.text || status.emoji}`}>{status.emoji ?? "💬"}</span>;
}

function CallButtons({ c }: { c: Conversation }) {
  const { org } = useOrg();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<CallMedia | null>(null);
  const [error, setError] = useState<string | null>(null);
  const label = c.kind === "channel" ? "Meet now" : "Call";
  async function go(media: CallMedia) {
    setBusy(media); setError(null);
    try {
      const r = await startCall(org.slug, c.id, media);
      navigate(`/o/${org.slug}/call/${r.call.id}`, { state: { join: r.join, call: r.call, media } satisfies CallNavState });
    } catch (err) { setError(errorText(err)); setBusy(null); }
  }
  return (
    <div className="relative flex items-center gap-1">
      <button type="button" onClick={() => go("video")} disabled={busy !== null} aria-label={`${label}: video`} title={c.kind === "channel" ? "Start a video meeting in this channel" : "Video call"}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-100 dark:hover:bg-brand-700/30">
        {busy === "video" ? <Spinner className="h-4 w-4" /> : <Icon name="video" className="h-4 w-4" />}<span className="hidden sm:inline">{c.kind === "channel" ? "Meet" : "Video"}</span>
      </button>
      {c.kind !== "channel" && (
        <button type="button" onClick={() => go("audio")} disabled={busy !== null} aria-label={`${label}: audio`} title="Audio call"
          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-100 dark:hover:bg-brand-700/30">
          {busy === "audio" ? <Spinner className="h-4 w-4" /> : <Icon name="phone" className="h-4 w-4" />}<span className="hidden sm:inline">Audio</span>
        </button>
      )}
      {error && <p role="alert" className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 shadow ring-1 ring-red-200 dark:bg-red-950 dark:text-red-200">{error}</p>}
    </div>
  );
}

// The card a call leaves in the conversation: live while it's going (Join), a summary once it's over.
function CallCardView({ m }: { m: Message }) {
  const { org, me } = useOrg();
  const navigate = useNavigate();
  const onCallScreen = useMatch("/o/:slug/call/:callId")?.params.callId;
  const call = m.call;
  if (!call) return null;
  const mine = m.author?.id === me.user.id;
  const live = call.status !== "ended";
  const kind = call.media === "video" ? "Video call" : "Audio call";
  const duration = call.answeredAt && call.endedAt ? formatDuration(Math.round((new Date(call.endedAt).getTime() - new Date(call.answeredAt).getTime()) / 1000)) : null;
  const summary = live
    ? call.status === "ringing" ? `${kind} · ringing…` : `${kind} in progress`
    : call.endReason === "missed" ? (mine ? `${kind} · no answer` : `Missed ${kind.toLowerCase()}`)
    : call.endReason === "declined" ? `${kind} · declined`
    : call.endReason === "cancelled" ? `${kind} · cancelled`
    : call.endReason === "failed" ? `${kind} · couldn't connect`
    : duration ? `${kind} · ${duration}` : `${kind} ended`;
  const missed = !live && !mine && (call.endReason === "missed" || call.endReason === "cancelled");
  return (
    <div className={cx("mt-1 inline-flex max-w-full items-center gap-3 rounded-xl px-3 py-2 ring-1", live ? "bg-emerald-50 ring-emerald-200 dark:bg-emerald-950/40 dark:ring-emerald-900" : missed ? "bg-red-50 ring-red-200 dark:bg-red-950/40 dark:ring-red-900" : "bg-white ring-slate-200 dark:bg-slate-800 dark:ring-slate-700")}>
      <span className={cx("grid h-8 w-8 shrink-0 place-items-center rounded-full", live ? "bg-emerald-600 text-white" : missed ? "bg-red-600 text-white" : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200")}>
        <Icon name={call.media === "video" ? "video" : "phone"} className="h-4 w-4" />
      </span>
      <span className="min-w-0 text-sm"><span className="block font-medium">{summary}</span><span className="block text-xs text-slate-500">{formatTime(call.startedAt)}</span></span>
      {live && onCallScreen !== call.id && <Button size="sm" onClick={() => navigate(`/o/${org.slug}/call/${call.id}`)}>Join</Button>}
    </div>
  );
}

// ---------------------------------------------------------------- history

function MessageList({ conversation, messages, names, firstUnread, loading, loadingOlder, hasOlder, onOlder, onThread }: {
  conversation: Conversation; messages: Message[]; names: Map<string, string>; firstUnread: number | null;
  loading: boolean; loadingOlder: boolean; hasOlder: boolean; onOlder: () => void; onThread: (id: string) => void;
}) {
  const { me, role } = useOrg();
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const prevFirst = useRef<string | null>(null);
  const prevHeight = useRef(0);
  const [awayFromBottom, setAwayFromBottom] = useState(false);

  // Older messages added above: keep what's on screen in place. New ones below: follow them if already at the bottom.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const first = messages[0]?.id ?? null;
    if (prevFirst.current && first !== prevFirst.current && prevHeight.current) el.scrollTop += el.scrollHeight - prevHeight.current;
    else if (atBottom.current) el.scrollTop = el.scrollHeight;
    prevFirst.current = first;
    prevHeight.current = el.scrollHeight;
  }, [messages]);

  // Images finishing loading change the height: stay at the bottom.
  useEffect(() => {
    const el = scroller.current;
    if (!el || !content.current) return;
    const ro = new ResizeObserver(() => { if (atBottom.current) el.scrollTop = el.scrollHeight; prevHeight.current = el.scrollHeight; });
    ro.observe(content.current);
    return () => ro.disconnect();
  }, []);

  function onScroll() {
    const el = scroller.current!;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    atBottom.current = distance < 80;
    setAwayFromBottom(distance > 400);
    if (el.scrollTop < 300 && hasOlder) onOlder();
  }

  const isModerator = conversation.kind === "channel" && atLeast(role, "admin");
  let unreadShown = false;

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={scroller} onScroll={onScroll} className="h-full overflow-y-auto" role="log" aria-label="Messages" aria-live="polite">
        <div ref={content} className="px-4 pb-2 pt-4">
          {loading && <div className="flex justify-center p-6 text-brand-600"><Spinner /></div>}
          {loadingOlder && <div className="flex justify-center p-2 text-brand-600"><Spinner className="h-4 w-4" /></div>}
          {!loading && !hasOlder && <Beginning c={conversation} />}
          {messages.map((m, i) => {
            const prev = messages[i - 1];
            const newDay = !prev || formatDay(prev.createdAt) !== formatDay(m.createdAt);
            const showUnread = !unreadShown && firstUnread !== null && m.seq !== null && m.seq >= firstUnread && m.author?.id !== me.user.id;
            if (showUnread) unreadShown = true;
            const compact = !newDay && !showUnread && prev && !prev.deleted && !m.deleted && prev.author?.id === m.author?.id
              && new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_MS;
            return (
              <div key={m.clientId ?? m.id}>
                {newDay && <Divider label={formatDay(m.createdAt)} />}
                {showUnread && <Divider label="New messages" tone="red" />}
                <MessageItem m={m} names={names} compact={Boolean(compact)} canModerate={isModerator} onThread={onThread} conversation={conversation} />
              </div>
            );
          })}
        </div>
      </div>
      {awayFromBottom && (
        <button type="button" onClick={() => { const el = scroller.current!; el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-brand-700 shadow-md ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-brand-100 dark:ring-slate-700">
          Jump to latest ↓
        </button>
      )}
    </div>
  );
}

function Beginning({ c }: { c: Conversation }) {
  return (
    <div className="mb-6 mt-2">
      <h2 className="text-lg font-semibold">{c.kind === "channel" ? `Welcome to #${c.title}` : c.title}</h2>
      <p className="text-sm text-slate-500">
        {c.project
          ? (c.project.audience === "client" ? `This is the project's chat with its client. Everyone on the project and its client are in it.` : `This is the project's own chat. Everyone on the project is in it, and comes and goes with it.`)
          : c.kind === "channel" ? `This is the start of the ${c.title} channel${c.team ? ` in ${c.team.name}` : ""}.` : c.kind === "group" ? "This is the start of your group chat." : "This is the start of your conversation."}
      </p>
    </div>
  );
}

function Divider({ label, tone = "slate" }: { label: string; tone?: "slate" | "red" }) {
  return (
    <div className="my-3 flex items-center gap-3" role="separator">
      <span className={cx("h-px flex-1", tone === "red" ? "bg-red-300 dark:bg-red-800" : "bg-slate-200 dark:bg-slate-800")} />
      <span className={cx("text-xs font-medium", tone === "red" ? "text-red-600" : "text-slate-500")}>{label}</span>
      <span className={cx("h-px flex-1", tone === "red" ? "bg-red-300 dark:bg-red-800" : "bg-slate-200 dark:bg-slate-800")} />
    </div>
  );
}

function TypingLine({ conversationId }: { conversationId: string }) {
  const typists = useTyping(conversationId);
  const first = (n: string) => n.split(" ")[0];
  const text = typists.length === 0 ? "" : typists.length === 1 ? `${first(typists[0]!.name)} is typing…` : typists.length === 2 ? `${first(typists[0]!.name)} and ${first(typists[1]!.name)} are typing…` : "Several people are typing…";
  return <div className="h-5 shrink-0 px-5 text-xs text-slate-500" aria-live="polite">{text}</div>;
}

// ---------------------------------------------------------------- one message

function MessageItem({ m, names, compact, canModerate, onThread, conversation, inThread }: {
  m: Message; names: Map<string, string>; compact: boolean; canModerate: boolean; onThread?: (id: string) => void; conversation: Conversation; inThread?: boolean;
}) {
  const { org, me } = useOrg();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [picker, setPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mine = m.author?.id === me.user.id;
  // The tools sit on hover, which a touch screen has no way to reach: there, a tap on the message opens them and a
  // tap anywhere else puts them away.
  const [tools, setTools] = useState(false);
  const row = useRef<HTMLDivElement>(null);
  const pointer = useRef("mouse");
  const closeTools = () => {
    setTools(false); setPicker(false);
    // A tool keeps the focus a touch leaves behind, and focus alone holds the strip open.
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && row.current?.contains(focused)) focused.blur();
  };
  useEffect(() => {
    if (!tools) return;
    const away = (e: PointerEvent) => { if (!row.current?.contains(e.target as Node)) closeTools(); };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [tools]);

  async function react(emoji: string) {
    closeTools();
    const on = !m.reactions.some((r) => r.emoji === emoji && r.userIds.includes(me.user.id));
    try {
      const r = await api<{ message: Message }>(`/orgs/${org.slug}/messages/${m.id}/reactions`, { method: "POST", body: { emoji, on } });
      patchMessage(qc, org.slug, m.conversationId, m.id, () => r.message);
    } catch (err) { setError(errorText(err)); }
  }
  async function remove() {
    closeTools();
    if (!confirm("Delete this message for everyone?")) return;
    try {
      await api(`/orgs/${org.slug}/messages/${m.id}`, { method: "DELETE" });
      patchMessage(qc, org.slug, m.conversationId, m.id, (x) => ({ ...x, deleted: true, body: "", reactions: [], files: [] }));
    } catch (err) { setError(errorText(err)); }
  }

  if (m.deleted) {
    return (
      <div className={cx("flex gap-3 px-1", compact ? "py-0.5" : "mt-2 py-1")}>
        <span className="w-9 shrink-0" />
        <p className="text-sm italic text-slate-400">This message was deleted.</p>
      </div>
    );
  }

  return (
    <div
      ref={row}
      onPointerDown={(e) => { pointer.current = e.pointerType; }}
      // On the click, not the pointer-up: the strip appears where the finger is, and a tap handled any earlier would
      // open it in time for its own click to land on the first reaction.
      onClick={(e) => {
        if (pointer.current === "mouse" || (e.target as HTMLElement).closest("button, a, input, textarea")) return;
        setTools((open) => !open);
      }}
      className={cx("group relative flex gap-3 rounded-lg px-1 hover:bg-slate-100/70 dark:hover:bg-slate-800/40", compact ? "py-0.5" : "mt-2 py-1", m.pending === "sending" && "opacity-60")}>
      <div className="w-9 shrink-0">
        {compact ? <span className={cx("block pt-1 text-right text-[10px] text-slate-400 group-hover:visible", tools ? "visible" : "invisible")}>{formatTime(m.createdAt)}</span> : m.author
          ? <PersonButton userId={m.author.id} label={m.author.name}><Avatar name={m.author.name} userId={m.author.id} /></PersonButton>
          : <Avatar name="?" />}
      </div>
      <div className="min-w-0 flex-1">
        {!compact && (
          <div className="flex items-baseline gap-2">
            {m.author ? <PersonButton userId={m.author.id} label={m.author.name} className="rounded text-sm font-semibold hover:underline">{m.author.name}</PersonButton> : <span className="text-sm font-semibold">Former member</span>}
            {m.author && <StatusEmoji userId={m.author.id} />}
            <time dateTime={m.createdAt} title={new Date(m.createdAt).toLocaleString()} className="text-xs text-slate-400">{formatTime(m.createdAt)}</time>
          </div>
        )}
        {editing ? <EditBox m={m} names={names} members={conversation.members} onDone={() => setEditing(false)} /> : (
          <>
            {m.kind === "call" && <CallCardView m={m} />}
            {m.body && <div className="whitespace-pre-wrap break-words text-sm leading-relaxed"><Body text={m.body} names={names} meId={me.user.id} />{m.editedAt && <span className="ml-1 text-xs text-slate-400">(edited)</span>}</div>}
            {m.files.length > 0 && <Files files={m.files} />}
          </>
        )}
        {m.reactions.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {m.reactions.map((r) => {
              const mineToo = r.userIds.includes(me.user.id);
              return (
                <button key={r.emoji} type="button" onClick={() => react(r.emoji)} title={r.userIds.map((id) => (id === me.user.id ? "You" : names.get(id) ?? "Someone")).join(", ")}
                  aria-pressed={mineToo} aria-label={`${r.emoji} ${r.count}`}
                  className={cx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1", mineToo ? "bg-brand-50 text-brand-700 ring-brand-300 dark:bg-brand-700/30 dark:text-brand-100 dark:ring-brand-700" : "bg-white ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:ring-slate-700")}>
                  <span>{r.emoji}</span>{r.count}
                </button>
              );
            })}
          </div>
        )}
        {!inThread && m.replyCount > 0 && onThread && (
          <button type="button" onClick={() => onThread(m.id)} className="mt-1 flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline">
            <Icon name="thread" className="h-3.5 w-3.5" />{m.replyCount} {m.replyCount === 1 ? "reply" : "replies"}{m.lastReplyAt && <span className="font-normal text-slate-400">· {formatTime(m.lastReplyAt)}</span>}
          </button>
        )}
        {m.pending === "failed" && <FailedSend m={m} />}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>

      {!m.pending && !editing && (
        <div className={cx("absolute -top-3 right-2 z-10 items-center gap-0.5 rounded-lg bg-white p-0.5 shadow-sm ring-1 ring-slate-200 group-focus-within:flex group-hover:flex dark:bg-slate-800 dark:ring-slate-700", tools ? "flex" : "hidden")}>
          {QUICK.slice(0, 3).map((e) => <button key={e} type="button" onClick={() => react(e)} aria-label={`React ${e}`} className="rounded-md px-1.5 py-1 text-sm hover:bg-slate-100 dark:hover:bg-slate-700">{e}</button>)}
          <ToolButton icon="smile" label="More reactions" onClick={() => setPicker((p) => !p)} />
          {!inThread && onThread && <ToolButton icon="reply" label="Reply in thread" onClick={() => { closeTools(); onThread(m.id); }} />}
          {mine && m.kind === "text" && <ToolButton icon="edit" label="Edit" onClick={() => { closeTools(); setEditing(true); }} />}
          {(mine || canModerate) && m.kind === "text" && <ToolButton icon="trash" label="Delete" onClick={remove} danger />}
        </div>
      )}
      {picker && <EmojiGrid onPick={react} onClose={() => setPicker(false)} className="absolute right-2 top-6 z-20" />}
    </div>
  );
}

function ToolButton({ icon, label, onClick, danger }: { icon: "smile" | "reply" | "edit" | "trash"; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}
      className={cx("rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700", danger ? "hover:text-red-600" : "hover:text-slate-800 dark:hover:text-slate-100")}>
      <Icon name={icon} className="h-4 w-4" />
    </button>
  );
}

// Text with @mentions and links (links only for http/https; everything else is plain text).
function Body({ text, names, meId }: { text: string; names: Map<string, string>; meId: string }) {
  const parts: ReactNode[] = [];
  const pattern = new RegExp(`${MENTION.source}|(https?:\\/\\/[^\\s<>"]+)`, "g");
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > last) parts.push(text.slice(last, index));
    if (match[1]) {
      const id = match[1];
      parts.push(<span key={index} className={cx("rounded px-0.5 font-medium", id === meId ? "bg-amber-100 text-amber-900 dark:bg-amber-500/25 dark:text-amber-100" : "bg-brand-50 text-brand-700 dark:bg-brand-700/30 dark:text-brand-100")}>@{names.get(id) ?? "someone"}</span>);
      last = index + match[0].length;
    } else {
      let url = match[0];
      const trailing = url.match(/[.,!?;:)\]]+$/)?.[0] ?? "";
      url = url.slice(0, url.length - trailing.length);
      parts.push(<a key={index} href={url} target="_blank" rel="noopener noreferrer nofollow" className="text-brand-600 underline decoration-brand-300 hover:decoration-brand-600">{url}</a>);
      last = index + url.length;
    }
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function Files({ files }: { files: Attachment[] }) {
  const { org } = useOrg();
  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {files.map((f) => f.contentType.startsWith("image/") ? (
        <a key={f.id} href={fileUrl(org.slug, f.id)} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg ring-1 ring-slate-200 dark:ring-slate-700">
          <img src={fileUrl(org.slug, f.id)} alt={f.filename} loading="lazy" className="max-h-64 max-w-xs object-contain" />
        </a>
      ) : (
        <a key={f.id} href={fileUrl(org.slug, f.id)} download={f.filename} className="flex max-w-xs items-center gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:ring-slate-700 dark:hover:bg-slate-700">
          <Icon name="file" className="h-6 w-6 shrink-0 text-brand-600" />
          <span className="min-w-0"><span className="block truncate text-sm font-medium">{f.filename}</span><span className="block text-xs text-slate-500">{formatSize(f.size)}</span></span>
          <Icon name="download" className="h-4 w-4 shrink-0 text-slate-400" />
        </a>
      ))}
    </div>
  );
}

// "@Name" in what people type becomes "<@id>" for the people in the conversation (longest names first).
function encodeMentions(text: string, members: { id: string; name: string }[]) {
  let out = text;
  for (const m of [...members].sort((a, b) => b.name.length - a.name.length)) out = out.split(`@${m.name}`).join(`<@${m.id}>`);
  return out;
}
const decodeMentions = (text: string, names: Map<string, string>) => text.replace(MENTION, (_m, id: string) => `@${names.get(id) ?? "someone"}`);

function EditBox({ m, names, members, onDone }: { m: Message; names: Map<string, string>; members: { id: string; name: string }[]; onDone: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [value, setValue] = useState(() => decodeMentions(m.body, names));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    if (!value.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await api<{ message: Message }>(`/orgs/${org.slug}/messages/${m.id}`, { method: "PATCH", body: { body: encodeMentions(value, members) } });
      patchMessage(qc, org.slug, m.conversationId, m.id, () => r.message);
      onDone();
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  }
  return (
    <div className="mt-1">
      <textarea value={value} onChange={(e) => setValue(e.target.value)} autoFocus rows={Math.min(8, value.split("\n").length + 1)} aria-label="Edit message"
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void save(); } if (e.key === "Escape") onDone(); }}
        className="block w-full rounded-lg border border-brand-500 bg-white px-3 py-2 text-sm dark:bg-slate-900" />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
        <Button size="sm" onClick={save} loading={busy}>Save</Button><Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
        <span>Enter to save · Esc to cancel</span>
      </div>
    </div>
  );
}

function FailedSend({ m }: { m: Message }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const retry = () => sendMessage(qc, org.slug, m.conversationId, { body: m.body, parentId: m.parentId ?? undefined, files: m.files, clientId: m.clientId! }, m.author!);
  const discard = () => removePending(qc, org.slug, m);
  return (
    <p className="mt-1 text-xs text-red-600">
      Not sent. <button type="button" onClick={() => void retry()} className="font-medium underline">Try again</button> · <button type="button" onClick={discard} className="underline">Discard</button>
    </p>
  );
}

// ---------------------------------------------------------------- sending

function placeMessage(qc: QueryClient, slug: string, m: Message) {
  if (m.parentId) qc.setQueryData<Replies>(keys.replies(slug, m.conversationId, m.parentId), (old) => old && { ...old, replies: upsertMessage(old.replies, m) });
  else addToPages(qc, slug, m.conversationId, m);
}

function setPending(qc: QueryClient, slug: string, m: Message, pending: Message["pending"]) {
  const patch = (list: Message[]) => list.map((x) => (x.clientId === m.clientId && x.pending ? { ...x, pending } : x));
  if (m.parentId) qc.setQueryData<Replies>(keys.replies(slug, m.conversationId, m.parentId), (old) => old && { ...old, replies: patch(old.replies) });
  else qc.setQueryData<{ pages: MessagePage[]; pageParams: unknown[] }>(keys.messages(slug, m.conversationId), (old) => old && { ...old, pages: old.pages.map((p) => ({ ...p, messages: patch(p.messages) })) });
}

function removePending(qc: QueryClient, slug: string, m: Message) {
  const drop = (list: Message[]) => list.filter((x) => !(x.clientId === m.clientId && x.pending));
  if (m.parentId) qc.setQueryData<Replies>(keys.replies(slug, m.conversationId, m.parentId), (old) => old && { ...old, replies: drop(old.replies) });
  else qc.setQueryData<{ pages: MessagePage[]; pageParams: unknown[] }>(keys.messages(slug, m.conversationId), (old) => old && { ...old, pages: old.pages.map((p) => ({ ...p, messages: drop(p.messages) })) });
}

// Shows the message straight away, then sends it; a retried send (same client id) is posted only once by the server.
async function sendMessage(qc: QueryClient, slug: string, conversationId: string, input: { body: string; parentId?: string; files: Attachment[]; clientId: string }, author: { id: string; name: string }) {
  const optimistic: Message = {
    id: `pending-${input.clientId}`, clientId: input.clientId, conversationId, seq: null, parentId: input.parentId ?? null, author,
    body: input.body, mentions: [], replyCount: 0, lastReplyAt: null, editedAt: null, deleted: false, createdAt: new Date().toISOString(),
    reactions: [], files: input.files, kind: "text", call: null, pending: "sending",
  };
  placeMessage(qc, slug, optimistic);
  try {
    const r = await api<{ message: Message }>(`/orgs/${slug}/conversations/${conversationId}/messages`, {
      method: "POST", body: { body: input.body, parentId: input.parentId, fileIds: input.files.map((f) => f.id), clientId: input.clientId },
    });
    placeMessage(qc, slug, r.message);
    return true;
  } catch {
    setPending(qc, slug, optimistic, "failed");
    return false;
  }
}

type Upload = { key: string; file: File; progress: number; attachment?: Attachment; error?: string; abort: AbortController };
type ComposerHandle = { addFiles: (files: File[]) => void };

const Composer = forwardRef<ComposerHandle, { conversation: Conversation; parentId?: string; placeholder: string; onSent?: () => void; autoFocus?: boolean; compact?: boolean }>(function Composer({ conversation, parentId, placeholder, onSent, autoFocus = true, compact = false }, ref) {
  const { org, me } = useOrg();
  const qc = useQueryClient();
  const live = useLive();
  const [value, setValue] = useState("");
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [mention, setMention] = useState<{ query: string; start: number; index: number } | null>(null);
  const [emoji, setEmoji] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const draftKey = `draft:${org.slug}:${conversation.id}:${parentId ?? ""}`;

  // Drafts survive switching conversations (this browser only).
  useEffect(() => {
    try { setValue(sessionStorage.getItem(draftKey) ?? ""); } catch { /* storage unavailable */ }
  }, [draftKey]);
  useEffect(() => {
    try { if (value) sessionStorage.setItem(draftKey, value); else sessionStorage.removeItem(draftKey); } catch { /* storage unavailable */ }
  }, [draftKey, value]);

  // Grow with the text, up to a limit; put the caret where an insertion asked for it, in the same frame as the new text
  // (a frame later, fast typing would already have landed somewhere else).
  const caretAt = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    if (caretAt.current !== null) {
      el.focus();
      el.setSelectionRange(caretAt.current, caretAt.current);
      caretAt.current = null;
    }
  }, [value]);

  const candidates = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return conversation.members.filter((m) => m.id !== me.user.id && m.name.toLowerCase().includes(q)).slice(0, 6);
  }, [mention, conversation.members, me.user.id]);

  function addFiles(files: File[]) {
    setError(null);
    const room = 10 - uploads.length;
    if (files.length > room) setError("You can attach up to 10 files to a message.");
    for (const file of files.slice(0, Math.max(0, room))) {
      const u: Upload = { key: newClientId(), file, progress: 0, abort: new AbortController() };
      setUploads((list) => [...list, u]);
      uploadFile(org.slug, file, (p) => setUploads((list) => list.map((x) => (x.key === u.key ? { ...x, progress: p } : x))), u.abort.signal)
        .then((attachment) => setUploads((list) => list.map((x) => (x.key === u.key ? { ...x, attachment, progress: 1 } : x))))
        .catch((err) => setUploads((list) => list.map((x) => (x.key === u.key ? { ...x, error: errorText(err) } : x))));
    }
  }
  useImperativeHandle(ref, () => ({ addFiles }));

  function onChange(text: string, caret: number) {
    setValue(text);
    if (text.trim()) live.sendTyping(conversation.id);
    const before = text.slice(0, caret);
    const match = before.match(/(?:^|\s)@([\p{L}\p{N}._' -]{0,30})$/u);
    if (match && !match[1]!.includes("  ")) setMention({ query: match[1]!, start: caret - match[1]!.length - 1, index: 0 });
    else setMention(null);
  }

  function pickMention(person: { id: string; name: string }) {
    if (!mention) return;
    const caret = mention.start + 1 + mention.query.length;
    caretAt.current = mention.start + person.name.length + 2;
    setValue(`${value.slice(0, mention.start)}@${person.name} ${value.slice(caret)}`);
    setMention(null);
  }

  function insert(text: string) {
    const el = input.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    caretAt.current = start + text.length;
    setValue(value.slice(0, start) + text + value.slice(end));
    setEmoji(false);
  }

  const uploading = uploads.some((u) => !u.attachment && !u.error);
  const ready = uploads.filter((u) => u.attachment).map((u) => u.attachment!);
  const canSend = !uploading && (value.trim().length > 0 || ready.length > 0);

  async function send() {
    if (!canSend) return;
    const body = encodeMentions(value.trim(), conversation.members);
    const files = ready;
    setValue(""); setUploads([]); setMention(null); setError(null);
    const ok = await sendMessage(qc, org.slug, conversation.id, { body, parentId, files, clientId: newClientId() }, { id: me.user.id, name: me.user.name });
    if (ok) onSent?.();
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (mention && candidates.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMention({ ...mention, index: (mention.index + 1) % candidates.length }); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMention({ ...mention, index: (mention.index - 1 + candidates.length) % candidates.length }); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pickMention(candidates[mention.index]!); return; }
      if (e.key === "Escape") { e.preventDefault(); setMention(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); }
  }

  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const files = [...e.clipboardData.files];
    if (files.length) { e.preventDefault(); addFiles(files); }
  }

  return (
    <div className="relative shrink-0 px-4 pb-4">
      {error && <div className="mb-2"><Alert>{error}</Alert></div>}
      {mention && candidates.length > 0 && (
        <ul role="listbox" aria-label="Mention someone" className="absolute bottom-full left-4 z-20 mb-1 w-72 overflow-hidden rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          {candidates.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === mention.index}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pickMention(p); }} className={cx("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm", i === mention.index ? "bg-brand-50 dark:bg-brand-700/30" : "hover:bg-slate-100 dark:hover:bg-slate-800")}>
                <Avatar name={p.name} userId={p.id} size="sm" />{p.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="rounded-xl bg-white ring-1 ring-slate-300 focus-within:ring-2 focus-within:ring-brand-500 dark:bg-slate-900 dark:ring-slate-700">
        {uploads.length > 0 && (
          <div className="flex flex-wrap gap-2 border-b border-slate-100 p-2 dark:border-slate-800">
            {uploads.map((u) => (
              <div key={u.key} className={cx("relative flex max-w-60 items-center gap-2 overflow-hidden rounded-lg py-1.5 pl-2 pr-7 text-xs ring-1", u.error ? "bg-red-50 ring-red-200 dark:bg-red-950/40 dark:ring-red-900" : "bg-slate-50 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700")}>
                <Icon name="file" className="h-4 w-4 shrink-0 text-brand-600" />
                <span className="min-w-0"><span className="block truncate font-medium">{u.file.name}</span><span className={cx("block", u.error ? "text-red-600" : "text-slate-500")}>{u.error ?? (u.attachment ? formatSize(u.attachment.size) : `${Math.round(u.progress * 100)}%`)}</span></span>
                {!u.attachment && !u.error && <span className="absolute bottom-0 left-0 h-0.5 bg-brand-500 transition-all" style={{ width: `${u.progress * 100}%` }} />}
                <button type="button" onClick={() => { u.abort.abort(); setUploads((list) => list.filter((x) => x.key !== u.key)); }} aria-label={`Remove ${u.file.name}`} className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700"><Icon name="x" className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={input} value={value} rows={1} placeholder={placeholder} aria-label={placeholder} autoFocus={autoFocus} maxLength={10_000}
          onChange={(e) => onChange(e.target.value, e.target.selectionStart)} onKeyDown={onKeyDown} onPaste={onPaste} onBlur={() => setTimeout(() => setMention(null), 150)}
          className="block max-h-[200px] w-full resize-none bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-slate-400"
        />
        <div className="flex items-center gap-1 px-2 pb-2">
          <button type="button" onClick={() => fileInput.current?.click()} aria-label="Attach files" title="Attach files" className="rounded-md p-2 [@media(pointer:coarse)]:p-3 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100"><Icon name="paperclip" className="h-5 w-5" /></button>
          <div className="relative">
            <button type="button" onClick={() => setEmoji((x) => !x)} aria-label="Emoji" title="Emoji" aria-expanded={emoji} className="rounded-md p-2 [@media(pointer:coarse)]:p-3 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100"><Icon name="smile" className="h-5 w-5" /></button>
            {emoji && <EmojiGrid onPick={insert} onClose={() => setEmoji(false)} className="absolute bottom-full left-0 z-20 mb-2" />}
          </div>
          <button type="button" onClick={() => insert("@")} aria-label="Mention someone" title="Mention someone" className="rounded-md px-2 py-1 text-sm font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100">@</button>
          {!parentId && !compact && <span className="ml-auto hidden text-xs text-slate-400 xl:inline">Enter to send · Shift+Enter for a new line</span>}
          <button type="button" onClick={() => void send()} disabled={!canSend} aria-label="Send" title="Send (Enter)"
            className="ml-auto rounded-lg xl:ml-2 bg-brand-600 p-2 text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-800">
            <Icon name="send" className="h-4 w-4" />
          </button>
        </div>
      </div>
      <input ref={fileInput} type="file" multiple hidden onChange={(e) => { addFiles([...(e.target.files ?? [])]); e.target.value = ""; }} />
    </div>
  );
});

function EmojiGrid({ onPick, onClose, className }: { onPick: (emoji: string) => void; onClose: () => void; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) onClose(); };
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [onClose]);
  return (
    <div ref={box} className={cx("grid w-72 grid-cols-8 gap-0.5 rounded-xl bg-white p-2 shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700", className)} role="dialog" aria-label="Choose an emoji">
      {EMOJI.map((e) => <button key={e} type="button" onClick={() => onPick(e)} className="rounded-md p-1 text-lg hover:bg-slate-100 dark:hover:bg-slate-800">{e}</button>)}
    </div>
  );
}

// ---------------------------------------------------------------- side panels

function ThreadPanel({ conversation, rootId, names, onClose }: { conversation: Conversation; rootId: string; names: Map<string, string>; onClose: () => void }) {
  const { org, role } = useOrg();
  const thread = useQuery({ queryKey: keys.replies(org.slug, conversation.id, rootId), queryFn: () => api<Replies>(`/orgs/${org.slug}/conversations/${conversation.id}/messages/${rootId}/replies`), retry: false });
  const end = useRef<HTMLDivElement>(null);
  const count = thread.data?.replies.length ?? 0;
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [count]);
  const canModerate = conversation.kind === "channel" && atLeast(role, "admin");
  return (
    <aside className="fixed inset-0 z-30 flex flex-col bg-white lg:static lg:z-auto lg:w-96 lg:shrink-0 lg:border-l lg:border-slate-200 dark:bg-slate-900 lg:dark:border-slate-800" aria-label="Thread">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
        <div><h2 className="font-semibold">Thread</h2><p className="text-xs text-slate-500">{conversation.title}</p></div>
        <button type="button" onClick={onClose} aria-label="Close thread" className="rounded-md p-2 [@media(pointer:coarse)]:p-3 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Icon name="x" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {thread.isLoading && <div className="flex justify-center p-6 text-brand-600"><Spinner /></div>}
        {thread.error && <Alert>{errorText(thread.error)}</Alert>}
        {thread.data && (
          <>
            <MessageItem m={thread.data.root} names={names} compact={false} canModerate={canModerate} conversation={conversation} inThread />
            <Divider label={count === 0 ? "No replies yet" : `${count} ${count === 1 ? "reply" : "replies"}`} />
            {thread.data.replies.map((m, i) => {
              const prev = thread.data!.replies[i - 1];
              const compact = Boolean(prev && prev.author?.id === m.author?.id && new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_MS);
              return <MessageItem key={m.clientId ?? m.id} m={m} names={names} compact={compact} canModerate={canModerate} conversation={conversation} inThread />;
            })}
            <div ref={end} />
          </>
        )}
      </div>
      {thread.data && !thread.data.root.deleted && !conversation.archived && <Composer conversation={conversation} parentId={rootId} placeholder="Reply…" />}
    </aside>
  );
}

function DetailsPanel({ conversation: c, meId, onClose }: { conversation: Conversation; meId: string; onClose: () => void }) {
  const { org, role } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const presence = usePresence();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(c.name ?? "");
  const [topic, setTopic] = useState(c.topic);
  const base = `/orgs/${org.slug}/conversations/${c.id}`;
  const canEditName = c.kind === "group" || (c.kind === "channel" && !c.isGeneral && (c.createdBy === meId || atLeast(role, "admin")));
  const canEditTopic = c.kind === "channel" && (c.createdBy === meId || atLeast(role, "admin"));
  const canRemove = c.kind !== "dm" && !c.isGeneral && (c.createdBy === meId || atLeast(role, "admin"));
  const canAdd = c.kind === "group" || (c.kind === "channel" && !c.isGeneral);
  const canLeave = c.kind === "group" || (c.kind === "channel" && !c.isGeneral);

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: keys.conversation(org.slug, c.id) }), qc.invalidateQueries({ queryKey: keys.conversations(org.slug) })]);
  const run = async (fn: () => Promise<unknown>) => { setError(null); try { await fn(); await refresh(); } catch (err) { setError(errorText(err)); } };

  return (
    <aside className="fixed inset-0 z-30 flex flex-col bg-white lg:static lg:z-auto lg:w-80 lg:shrink-0 lg:border-l lg:border-slate-200 dark:bg-slate-900 lg:dark:border-slate-800" aria-label="Details">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 px-4 dark:border-slate-800">
        <h2 className="font-semibold">Details</h2>
        <button type="button" onClick={onClose} aria-label="Close details" className="rounded-md p-2 [@media(pointer:coarse)]:p-3 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Icon name="x" /></button>
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        {error && <Alert>{error}</Alert>}
        {canEditName && (
          <form onSubmit={(e) => { e.preventDefault(); void run(() => api(base, { method: "PATCH", body: { name } })); }} className="space-y-1.5">
            <label htmlFor="conv-name" className="block text-sm font-medium">{c.kind === "channel" ? "Channel name" : "Group name"}</label>
            <div className="flex gap-2"><input id="conv-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="No name" className="block min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900" /><Button size="sm" type="submit" variant="secondary">Save</Button></div>
          </form>
        )}
        {canEditTopic && (
          <form onSubmit={(e) => { e.preventDefault(); void run(() => api(base, { method: "PATCH", body: { topic } })); }} className="space-y-1.5">
            <label htmlFor="conv-topic" className="block text-sm font-medium">Topic</label>
            <div className="flex gap-2"><input id="conv-topic" value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={250} placeholder="What's this channel about?" className="block min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900" /><Button size="sm" type="submit" variant="secondary">Save</Button></div>
          </form>
        )}
        <label className="flex items-center justify-between gap-3 text-sm">
          <span><span className="font-medium">Mute</span><br /><span className="text-slate-500">No unread badge or notifications, except when someone mentions you.</span></span>
          <input type="checkbox" checked={c.muted} onChange={(e) => run(() => api(base, { method: "PATCH", body: { muted: e.target.checked } }))} className="h-4 w-4 rounded" />
        </label>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">People ({c.members.length})</h3>
            {canAdd && <Button size="sm" variant="ghost" onClick={() => setAdding(true)}><Icon name="plus" className="h-4 w-4" />Add</Button>}
          </div>
          <ul className="space-y-1">
            {c.members.map((m) => (
              <li key={m.id} className="flex items-center gap-2.5 rounded-lg px-1 py-1">
                <PersonButton userId={m.id} label={m.name} className="relative"><Avatar name={m.name} userId={m.id} size="sm" /><PresenceDot status={presence[m.id]} userId={m.id} className="absolute -bottom-0.5 -right-0.5" /></PersonButton>
                <span className="min-w-0 flex-1 truncate text-sm"><PersonButton userId={m.id} label={m.name} className="rounded hover:underline">{m.name}</PersonButton>{m.id === meId && <span className="text-slate-500"> (you)</span>}<StatusEmoji userId={m.id} /></span>
                {m.id === c.createdBy && c.kind !== "dm" && <Badge>Creator</Badge>}
                {canRemove && m.id !== meId && (
                  <button type="button" onClick={() => { if (confirm(`Remove ${m.name}?`)) void run(() => api(`${base}/members/${m.id}`, { method: "DELETE" })); }} aria-label={`Remove ${m.name}`} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"><Icon name="x" className="h-4 w-4" /></button>
                )}
              </li>
            ))}
          </ul>
        </div>
        {canLeave && (
          <Button variant="secondary" className="w-full text-red-600" onClick={() => { if (confirm(`Leave ${c.title}?`)) void run(async () => { await api(`${base}/members/me`, { method: "DELETE" }); navigate(`/o/${org.slug}/chat`); }); }}>
            Leave {c.kind === "channel" ? "channel" : "group"}
          </Button>
        )}
      </div>
      <AddPeopleDialog conversation={c} open={adding} onClose={() => setAdding(false)} />
    </aside>
  );
}
